import { HandCoins, type LucideIcon, Send } from 'lucide-react'
import { SearchTabs } from 'components/ds'
import type { SearchTabItem } from 'components/ds'
import { cn } from 'utils/cn'
import { LocationData } from 'features/load-hub/types/filters'
import { formatLocation } from 'features/load-hub/utils/tabDerivation'
import { BoxTruckTab } from '../hooks/useBoxTruckTabs'
import { legOptions } from '../hooks/requestMapping'
import type { BoxTruckPinnedBoard } from '../utils/pinnedBoard'

/** Tab label for a leg: one chip → its city/state label; multiple states → a compact "TX, CA +2". Empty → ''
 *  (the caller substitutes 'Anywhere' / 'New Search'). */
const legLabel = (options: LocationData[] | undefined, single: LocationData | null): string => {
    const opts = legOptions(options, single)
    if (opts.length === 0) return ''
    if (opts.length === 1) return formatLocation(opts[0])
    // A zone chip has no stateProv — fall back to its zone code so it shows in the multi-leg label too.
    const codes = opts.map((o) => o.stateProv || o.zoneCode).filter((s): s is string => !!s)
    return codes.length <= 3 ? codes.join(', ') : `${codes.slice(0, 2).join(', ')} +${codes.length - 2}`
}

/** The pinned pills, in strip order. One entry per board rather than a prop pair each, so the strip, the
 *  count badge and the selected state cannot learn about a new pinned board one at a time. */
const PINNED_PILLS: { board: BoxTruckPinnedBoard; label: string; title: string; Icon: LucideIcon }[] = [
    { board: 'driver_bids', label: 'Driver Bids', title: 'Every load this company’s drivers have bid on', Icon: HandCoins },
    { board: 'my_bids', label: 'My Biddings', title: 'Loads you have sent a bid email about', Icon: Send },
]

interface BoxTruckTabsBarProps {
    tabs: BoxTruckTab[]
    activeTabId: string
    /** Visible-row count of the ACTIVE board (search tab or a pinned board). */
    activeCount: number
    onSelect: (id: string) => void
    onRemove: (id: string) => void
    onAdd: () => void
    onCloseAll: () => void
    /** Which pinned board is the active one, or null while a search tab is. */
    pinnedBoard: BoxTruckPinnedBoard | null
    onPinnedSelect: (board: BoxTruckPinnedBoard) => void
    /** Drag-reorder the search tabs (full new id order). */
    onReorder: (orderedIds: string[]) => void
    /** Rename a tab (double-click inline edit; '' clears the custom name back to the lane label). */
    onRename: (id: string, label: string) => void
    /** Open the active Numeo search workspace in a separate top-level browser tab. */
    onOpenActiveTab: () => void
}

/** The Box Truck search-tab strip — the pinned board pills (Driver Bids, My Biddings; never removable, NEVER
 *  the default view) followed by the shared DS SearchTabs (Load Hub's `pill` variant). A search tab labels
 *  itself by its last-searched lane; an unsearched tab reads "New Search / Anywhere". The pinned pills live
 *  OUTSIDE SearchTabs so they can't grow a close button or be swept by "Close all". */
export const BoxTruckTabsBar = ({
    tabs,
    activeTabId,
    activeCount,
    onSelect,
    onRemove,
    onAdd,
    onCloseAll,
    pinnedBoard,
    onPinnedSelect,
    onReorder,
    onRename,
    onOpenActiveTab,
}: BoxTruckTabsBarProps) => {
    const items: SearchTabItem[] = tabs.map((t) => {
        const origin = legLabel(t.filters.originOptions, t.filters.origin)
        return {
            id: t.id,
            // A custom name (driver-select auto-name or manual rename) wins; otherwise the tab labels itself
            // by its lane — an unsearched tab reads "New Search", a searched anywhere tab reads "Anywhere".
            primary: t.label || origin || (t.searched ? 'Anywhere' : 'New Search'),
            // Named tab: show the lane origin as the secondary line so the route stays visible under the name.
            secondary: (t.label ? origin : '') || legLabel(t.filters.destinationOptions, t.filters.destination) || 'Anywhere',
            count: !pinnedBoard && t.id === activeTabId && t.searched ? activeCount : undefined,
        }
    })
    return (
        <div className="flex items-stretch">
            {PINNED_PILLS.map(({ board, label, title, Icon }) => {
                const active = pinnedBoard === board
                return (
                    <button
                        key={board}
                        type="button"
                        onClick={() => onPinnedSelect(board)}
                        aria-selected={active}
                        role="tab"
                        title={title}
                        className={cn(
                            'my-1.5 mr-1 flex shrink-0 cursor-pointer items-center gap-1.5 self-center rounded-lg border-none px-3 py-1.5 text-[12px] font-semibold transition-colors',
                            active ? 'bg-accent-soft text-accent' : 'bg-transparent text-foreground-muted hover:bg-default hover:text-foreground'
                        )}
                    >
                        <Icon size={14} />
                        {label}
                        {active && <span className="rounded-full bg-accent-soft px-1.5 text-[11px] font-semibold text-accent">{activeCount}</span>}
                    </button>
                )
            })}
            <div className="w-px self-stretch bg-border" />
            <div className="min-w-0 flex-1">
                <SearchTabs
                    variant="pill"
                    tabs={items}
                    // While a pinned board is on screen, no search tab reads as selected.
                    activeTabId={pinnedBoard ? null : activeTabId}
                    onSelectTab={onSelect}
                    onRemoveTab={tabs.length > 1 ? onRemove : undefined}
                    onAddTab={onAdd}
                    onCloseAllTabs={onCloseAll}
                    onReorderTabs={onReorder}
                    onRenameTab={onRename}
                    onOpenActiveTab={onOpenActiveTab}
                />
            </div>
        </div>
    )
}
